import type { AiSecuritySignalReviewState, AiSecuritySignalRunSummary } from "@closer/shared";
import {
  AI_CERTAINTY_WARNING,
  AI_SECURITY_SIGNAL_ADVISORY,
  AI_SECURITY_SIGNAL_EMPTY_MESSAGE,
  AI_SECURITY_SIGNAL_PRIORITY_NAMES,
  AI_SECURITY_SIGNAL_REVIEW_STATE_NAMES,
} from "@closer/shared";
import { Badge, Button, Card, InfoBanner, Skeleton, useToast } from "@closer/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { AiAdvisoryNotice } from "../../components/AiOutputNotice";
import { StateView } from "../../components/StateView";
import { api } from "../../lib/apiClient";
import { useAuth } from "../../lib/authContext";
import { formatDate } from "../../lib/format";
import { usePreferences } from "../../lib/preferences";
import { tenantKey } from "../../lib/tenantQuery";
import { resolveErrorState } from "../../lib/uiState";
import { useIdempotencyKey } from "../../lib/useIdempotencyKey";

/**
 * HESAP GÜVENLİĞİ AI SİNYALLERİ (CLOS-287 `[191]` · CLOS-1011).
 *
 * ### ⚠ PANELDE TEK TIKLA YAPTIRIM YOK
 *
 * Kart: *"panelde tek tıkla otomatik enforcement yoktur."* Bu bölümde oturum kapatan,
 * hesap kilitleyen ya da 2FA açan TEK BİR düğme yok. Sinyalin önerdiği adım bir CÜMLE;
 * kullanıcı onu okuyup aynı sayfadaki kanonik akışa (oturumlar, şifre, 2FA) kendisi
 * gidiyor. Bir "Tüm oturumları kapat" düğmesi koysaydık, AI'ın bir tahmini kullanıcıyı
 * geri alınamaz bir işleme bir tık uzağa getirirdi.
 *
 * ### ⚠ KESİN DİL YASAK
 *
 * *"fraud/suç/ihlal gibi kesin hüküm dili kullanılmaz."* Uyarı cümlesi şemadan geliyor ve
 * her sonucun üstünde duruyor; öncelik rozetleri de eylem değil İNCELEME dili taşıyor
 * ("Önce bunu inceleyin"), renk tek başına anlam taşımıyor.
 *
 * ### ⚠ BOŞ SONUÇ "GÜVENDESİNİZ" DEMİYOR
 *
 * `AI_SECURITY_SIGNAL_EMPTY_MESSAGE` açıkça *"Bu, hesabınızda hiçbir risk olmadığı
 * anlamına gelmez"* diyor — güvenlik alanında sahte bir güvence, hiç sinyal vermemekten
 * daha zararlı.
 */
export function AiSecuritySignalsSection() {
  const { activeTenantId } = useAuth();
  const queryClient = useQueryClient();
  const toast = useToast();
  const { preferences } = usePreferences();

  /**
   * ⚠ Aktif çalışma alanı YOKSA bölüm HİÇ çizilmiyor — ve bu sessiz bir gizleme değil:
   * sinyal üretmek AI kredisi harcıyor, kredi kiracıya ait. Hiçbir organizasyona üye
   * olmayan kullanıcı güvenlik GEÇMİŞİNİ görüyor (o ücretsiz), sinyalleri göremiyor.
   */
  const tenantId = activeTenantId ?? undefined;

  const anahtar = tenantKey(tenantId ?? "", "guvenlik-sinyalleri");
  const liste = useQuery({
    queryKey: anahtar,
    enabled: tenantId !== undefined,
    queryFn: async () =>
      (await api<AiSecuritySignalRunSummary[]>(`/api/session/security-signals`, { tenantId })).data,
  });

  /** ⚠ Anahtar NİYETE bağlı: çift tıklama aynı anahtarı taşır (BUG-004). */
  const idempotency = useIdempotencyKey();

  const uret = useMutation({
    mutationFn: async () =>
      (
        await api<AiSecuritySignalRunSummary>(`/api/session/security-signals`, {
          method: "POST",
          tenantId,
          body: JSON.stringify({}),
          headers: { "idempotency-key": idempotency.uret() },
        })
      ).data,
    onSuccess: async () => {
      /** ⚠ Başarıdan sonra YENİ niyet yeni anahtar alıyor; yoksa kullanıcının bilinçli
          ikinci işlemi replay sayılıp sessizce ilk yanıta düşerdi (BUG-004). */
      idempotency.yenile();
      await queryClient.invalidateQueries({ queryKey: anahtar });
      await queryClient.invalidateQueries({
        queryKey: tenantKey(tenantId ?? "", "subscription-overview"),
      });
    },
    onError: (err: unknown) =>
      toast.error(err instanceof Error ? err.message : "Sinyaller üretilemedi"),
  });

  const durumGuncelle = useMutation({
    mutationFn: async (girdi: {
      runId: string;
      index: number;
      reviewState: AiSecuritySignalReviewState;
    }) =>
      (
        await api<AiSecuritySignalRunSummary>(`/api/session/security-signals/${girdi.runId}`, {
          method: "PATCH",
          tenantId,
          body: JSON.stringify({ index: girdi.index, reviewState: girdi.reviewState }),
        })
      ).data,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: anahtar });
    },
    onError: (err: unknown) =>
      toast.error(err instanceof Error ? err.message : "Sinyal güncellenemedi"),
  });

  if (tenantId === undefined) return null;

  const sonCalisma = (liste.data ?? []).find((c) => c.status === "completed") ?? null;

  return (
    <Card>
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <h2 className="mr-auto text-base font-semibold text-fg-heading">
          Hesap güvenliği sinyalleri
        </h2>
        <Button
          type="button"
          size="sm"
          variant="secondary"
          loading={uret.isPending}
          disabled={uret.isPending}
          onClick={() => uret.mutate()}
        >
          {uret.isPending ? "İnceleniyor…" : sonCalisma ? "Yeniden incele" : "Geçmişimi incele"}
        </Button>
      </div>

      <AiAdvisoryNotice />
      {/* ⚠ Kartın zorunlu cümlesi: bu bir güvenlik kararı ya da suçlama değil. */}
      <p className="mt-1 text-sm text-fg-muted" data-testid="ai-guvenlik-uyari">
        {AI_SECURITY_SIGNAL_ADVISORY}
      </p>

      <div className="mt-4">
        {liste.isPending ? (
          <Skeleton lines={3} />
        ) : liste.error ? (
          <StateView
            state={resolveErrorState(liste.error, {
              idempotent: true,
              overrides: {
                error: {
                  title: "Sinyaller yüklenemedi",
                  description: "Bölüm şu an getirilemedi.",
                },
              },
            })}
            onRetry={() => void liste.refetch()}
          />
        ) : !sonCalisma ? (
          <p className="text-sm text-fg-muted">
            Hesabınızın güvenlik geçmişini AI ile inceleyip dikkat çeken örüntüleri
            listeleyebilirsiniz.
          </p>
        ) : (sonCalisma.output?.signals.length ?? 0) === 0 ? (
          /* ⚠ "Güvendesiniz" DEMİYOR: sahte güvence hiç sinyal vermemekten zararlı. */
          <p className="text-sm text-fg-muted" data-testid="ai-guvenlik-bos">
            {AI_SECURITY_SIGNAL_EMPTY_MESSAGE}
          </p>
        ) : (
          <div className="flex flex-col gap-3">
            <div className="flex flex-wrap items-center gap-2 text-sm text-fg-muted">
              <Badge tone="neutral">{sonCalisma.model}</Badge>
              <span className="tabular-nums">{sonCalisma.credits} kredi</span>
              <span>{sonCalisma.eventCount} olay incelendi</span>
              <span>{formatDate(sonCalisma.createdAt, preferences.locale)}</span>
            </div>

            {sonCalisma.output!.signals.map((sinyal, i) => {
              const durum = sonCalisma.reviewStates[String(i)] ?? "open";
              return (
                <article
                  key={i}
                  data-testid={`ai-guvenlik-sinyal-${i}`}
                  className="rounded-lg border border-border-subtle p-3"
                >
                  <div className="flex flex-wrap items-center gap-2">
                    {/* ⚠ Öncelik METİNLE: renk tek başına anlam taşımıyor. */}
                    <Badge tone={sinyal.priority === "high" ? "warning" : "neutral"}>
                      {AI_SECURITY_SIGNAL_PRIORITY_NAMES[sinyal.priority]}
                    </Badge>
                    {durum !== "open" ? (
                      <Badge tone="neutral">{AI_SECURITY_SIGNAL_REVIEW_STATE_NAMES[durum]}</Badge>
                    ) : null}
                    <span className="text-xs text-fg-muted">
                      {sinyal.windowStart} – {sinyal.windowEnd} · {sinyal.eventCount} olay
                    </span>
                  </div>

                  <h3 className="mt-2 text-sm font-semibold text-fg-heading">{sinyal.title}</h3>
                  <p className="mt-1 text-sm text-fg">{sinyal.pattern}</p>
                  {/*
                  ⚠ KESİN İFADE İŞARETİ (CLOS-1086). Cümle silinmiyor ya da düzeltilmiyor —
                  yanına ne olduğu yazılıyor. Sessizce düzeltmek AI'ın ne dediğini
                  kullanıcıdan gizlemek olurdu; reddetmek kullanıcının kredisini bir
                  kelime seçimi yüzünden yakardı. Risk ailesindeki kararla aynı.
                */}
                  {sonCalisma.certaintyFlaggedIndexes.includes(i) ? (
                    <p className="mt-1 text-xs text-warning" data-testid="ai-guvenlik-kesinlik">
                      {AI_CERTAINTY_WARNING}
                    </p>
                  ) : null}

                  {/* ⚠ ÖNERİLEN ADIM BİR CÜMLE, BİR DÜĞME DEĞİL: yaptırım kullanıcının kendi eliyle. */}
                  <p className="mt-2 rounded-md bg-canvas p-2 text-sm text-fg-strong">
                    Önerilen inceleme adımı: {sinyal.suggestedStep}
                  </p>

                  <div className="mt-2 flex flex-wrap gap-2">
                    {durum !== "reviewed" ? (
                      <Button
                        type="button"
                        size="sm"
                        variant="secondary"
                        disabled={durumGuncelle.isPending}
                        onClick={() =>
                          durumGuncelle.mutate({
                            runId: sonCalisma.id,
                            index: i,
                            reviewState: "reviewed",
                          })
                        }
                      >
                        İnceledim
                      </Button>
                    ) : null}
                    {durum !== "dismissed" ? (
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        disabled={durumGuncelle.isPending}
                        onClick={() =>
                          durumGuncelle.mutate({
                            runId: sonCalisma.id,
                            index: i,
                            reviewState: "dismissed",
                          })
                        }
                      >
                        Yok say
                      </Button>
                    ) : (
                      <Button
                        type="button"
                        size="sm"
                        variant="secondary"
                        disabled={durumGuncelle.isPending}
                        onClick={() =>
                          durumGuncelle.mutate({
                            runId: sonCalisma.id,
                            index: i,
                            reviewState: "open",
                          })
                        }
                      >
                        Geri al
                      </Button>
                    )}
                  </div>
                </article>
              );
            })}

            {sonCalisma.output!.reviewNeeded.length > 0 ? (
              <InfoBanner tone="warning">
                <div data-testid="ai-guvenlik-belirsizlik">
                  <p className="font-medium">Kesin olmayan noktalar</p>
                  <ul className="mt-1 list-disc pl-5 text-sm">
                    {sonCalisma.output!.reviewNeeded.map((satir, i) => (
                      <li key={i}>{satir}</li>
                    ))}
                  </ul>
                </div>
              </InfoBanner>
            ) : null}
          </div>
        )}
      </div>
    </Card>
  );
}
